import { FormSection } from '@buyerflo/ui/components/FormSection'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { DatePicker } from 'components/datePicker'
import { Check, ErrorOutlineRounded, ScheduleSendRounded } from 'components/icons'
import Input from 'components/input'
import { format } from 'date-fns'
import { forwardRef, useEffect, useImperativeHandle, useState } from 'react'
import { Spinner, Text, View, XStack, YStack } from 'tamagui'
import { useTRPC } from 'utils/trpc'
import { CLIENT_DETAIL_MAX_WIDTH } from './helpers'
import { usePurchaserContext } from './purchaserProvider'

type InvoiceState = 'sent' | 'paid' | 'voided' | 'error'

/** 9am AEST on a given date = 23:00 UTC on the previous day */
const getSendTimestamp = (sendDate: string): number => {
    const [year, month, day] = sendDate.split('-').map(Number)
    return Date.UTC(year, month - 1, day) - 1 * 60 * 60 * 1000
}

const formatCountdown = (sendDate: string): string => {
    const diff = getSendTimestamp(sendDate) - Date.now()
    if (diff <= 0) return 'Sending now...'
    const totalMinutes = Math.floor(diff / (1000 * 60))
    const days = Math.floor(totalMinutes / (60 * 24))
    const hours = Math.floor((totalMinutes % (60 * 24)) / 60)
    const minutes = totalMinutes % 60
    if (days > 0) return `Sending in ${days}d ${hours}h`
    if (hours > 0) return `Sending in ${hours}h ${minutes}m`
    return `Sending in ${minutes}m`
}

const getInvoiceState = (
    invoice: { xeroInvoiceId: string; xeroStatus?: string; sendError?: string } | undefined,
): InvoiceState | undefined => {
    if (!invoice) return undefined
    if (invoice.sendError) return 'error'
    if (invoice.xeroStatus === 'VOIDED' || invoice.xeroStatus === 'DELETED') return 'voided'
    if (invoice.xeroStatus === 'PAID') return 'paid'
    return 'sent'
}

const PurchasedProperty = forwardRef(({}, ref: any) => {
    const trpc = useTRPC()
    const { purchaser, setPurchaser, refreshPurchaser } = usePurchaserContext()
    const queryClient = useQueryClient()
    const updatePurchasedPropertyMutation = useMutation(trpc.purchaser.updatePurchasedProperty.mutationOptions())

    // Saving the purchased property writes a new audit log entry server-side, so
    // mark the cached audit log stale to force a refetch next time the tab is viewed.
    const invalidateAuditLog = () =>
        queryClient.invalidateQueries({
            queryKey: trpc.purchaser.getAuditLog.queryKey({ purchaserId: purchaser.purchaserId }),
        })
    const sendCommissionMutation = useMutation(
        trpc.xero.sendCommissionInvoice.mutationOptions({
            onSuccess: () => refreshPurchaser(),
        }),
    )

    const [contractPrice, setContractPrice] = useState(purchaser.contractPrice ?? '')
    const [unconditionalDate, setUnconditionalDate] = useState<string | undefined>(
        purchaser.unconditionalDate ?? undefined,
    )
    const [propertyAddress, setPropertyAddress] = useState(purchaser.propertyAddress ?? '')

    const saveForm = async (): Promise<boolean> => {
        if (!contractPrice && !unconditionalDate && !propertyAddress) return true
        try {
            const updatedPurchaser = await updatePurchasedPropertyMutation.mutateAsync({
                purchaserId: purchaser.purchaserId,
                data: {
                    contractPrice: contractPrice ? Number(contractPrice) : 0,
                    unconditionalDate: unconditionalDate,
                    propertyAddress: propertyAddress || undefined,
                },
            })
            setPurchaser(updatedPurchaser)
            invalidateAuditLog()
            return true
        } catch {
            return false
        }
    }

    useImperativeHandle(ref, () => ({
        saveForm,
        resetForm: () => {
            setContractPrice(purchaser.contractPrice ?? '')
            setUnconditionalDate(purchaser.unconditionalDate ?? '')
            setPropertyAddress(purchaser.propertyAddress ?? '')
        },
    }))

    const commissionInvoice = purchaser.commissionInvoice
    const invoiceState = getInvoiceState(commissionInvoice)
    const isScheduled = !commissionInvoice && !!purchaser.unconditionalDate
    const [, setTick] = useState(0)

    useEffect(() => {
        if (!isScheduled) return
        const interval = setInterval(() => setTick(t => t + 1), 60_000)
        return () => clearInterval(interval)
    }, [isScheduled])

    const XeroLink = () =>
        commissionInvoice?.xeroInvoiceId ? (
            <Text
                fontSize="$2"
                cursor="pointer"
                color="$primary"
                textDecorationLine="underline"
                marginTop="$2"
                onPress={() =>
                    window.open(
                        `https://go.xero.com/AccountsReceivable/View.aspx?InvoiceID=${commissionInvoice.xeroInvoiceId}`,
                        '_blank',
                    )
                }
            >
                View in Xero
            </Text>
        ) : null

    return (
        <YStack
            gap="$4"
            maxWidth={CLIENT_DETAIL_MAX_WIDTH}
            paddingBottom="$8"
        >
            <FormSection title="Property Details">
                <Input
                    label="Property Address"
                    value={propertyAddress}
                    onChange={setPropertyAddress}
                />
                <Input
                    label="Contract Price"
                    type="dollar"
                    value={contractPrice}
                    onChange={v => setContractPrice(v ? Number(v) : '')}
                    slotProps={{ input: { min: 0, step: 1000 } }}
                />
                <DatePicker
                    label="Unconditional Date"
                    value={unconditionalDate}
                    onChange={v => setUnconditionalDate(v ?? undefined)}
                />
            </FormSection>

            {(commissionInvoice || purchaser.unconditionalDate) && (
                <FormSection title="Commission Invoice">
                    <View>
                        {isScheduled && purchaser.unconditionalDate && (
                            <View
                                backgroundColor="$statusOkSoft"
                                padding="$3"
                                borderRadius="$3"
                            >
                                <XStack
                                    gap="$2"
                                    alignItems="center"
                                >
                                    <ScheduleSendRounded
                                        size={16}
                                        color="$statusOk"
                                    />
                                    <YStack
                                        gap="$1"
                                        flex={1}
                                    >
                                        <Text>
                                            Invoice will be created and sent on{' '}
                                            {format(
                                                new Date(purchaser.unconditionalDate + 'T00:00:00'),
                                                'dd MMM yyyy',
                                            )}{' '}
                                            at 9:00am AEST
                                        </Text>
                                        <XStack
                                            gap="$2"
                                            alignItems="center"
                                        >
                                            {/*<Text fontSize="$2" color="$blue11">
                                                {formatCountdown(purchaser.unconditionalDate)}
                                            </Text>
                                            <Text fontSize="$2" color="$gray11">
                                                •
                                            </Text>*/}
                                            {sendCommissionMutation.isPending ? (
                                                <XStack
                                                    gap="$1"
                                                    alignItems="center"
                                                >
                                                    <Spinner size="small" />
                                                    <Text
                                                        fontSize="$2"
                                                        color="$gray11"
                                                    >
                                                        Sending...
                                                    </Text>
                                                </XStack>
                                            ) : (
                                                <Text
                                                    fontSize="$2"
                                                    cursor="pointer"
                                                    color="$primary"
                                                    textDecorationLine="underline"
                                                    onPress={() =>
                                                        sendCommissionMutation.mutate({
                                                            purchaserId: purchaser.purchaserId,
                                                        })
                                                    }
                                                >
                                                    Send now
                                                </Text>
                                            )}
                                        </XStack>
                                    </YStack>
                                </XStack>
                            </View>
                        )}

                        {invoiceState === 'sent' && (
                            <View
                                backgroundColor="$statusOkSoft"
                                padding="$3"
                                borderRadius="$3"
                            >
                                <XStack
                                    gap="$2"
                                    alignItems="center"
                                >
                                    <Check
                                        size={16}
                                        color="$statusOk"
                                    />
                                    <Text>Commission invoice sent</Text>
                                </XStack>
                                <XeroLink />
                            </View>
                        )}

                        {invoiceState === 'paid' && (
                            <View
                                backgroundColor="$statusOkSoft"
                                padding="$3"
                                borderRadius="$3"
                            >
                                <XStack
                                    gap="$2"
                                    alignItems="center"
                                >
                                    <Check
                                        size={16}
                                        color="$statusOk"
                                    />
                                    <Text fontWeight="600">Commission invoice paid</Text>
                                </XStack>
                                <XeroLink />
                            </View>
                        )}

                        {invoiceState === 'voided' && (
                            <View
                                backgroundColor="$statusNeutralSoft"
                                padding="$3"
                                borderRadius="$3"
                            >
                                <XStack
                                    gap="$2"
                                    alignItems="center"
                                >
                                    <ErrorOutlineRounded
                                        size={16}
                                        color="$colorMuted"
                                    />
                                    <Text color="$colorMuted">Commission invoice voided</Text>
                                </XStack>
                            </View>
                        )}

                        {invoiceState === 'error' && (
                            <View
                                backgroundColor="$statusDangerSoft"
                                padding="$3"
                                borderRadius="$3"
                            >
                                <XStack
                                    gap="$2"
                                    alignItems="center"
                                >
                                    <ErrorOutlineRounded
                                        size={16}
                                        color="$statusDanger"
                                    />
                                    <YStack
                                        gap="$1"
                                        flex={1}
                                    >
                                        <Text>Commission invoice failed</Text>
                                        <Text
                                            fontSize="$2"
                                            color="$statusDanger"
                                        >
                                            {commissionInvoice?.sendError}
                                        </Text>
                                        {sendCommissionMutation.isPending ? (
                                            <XStack
                                                gap="$1"
                                                alignItems="center"
                                            >
                                                <Spinner size="small" />
                                                <Text
                                                    fontSize="$2"
                                                    color="$gray11"
                                                >
                                                    Retrying...
                                                </Text>
                                            </XStack>
                                        ) : (
                                            <Text
                                                fontSize="$2"
                                                cursor="pointer"
                                                color="$primary"
                                                textDecorationLine="underline"
                                                onPress={() =>
                                                    sendCommissionMutation.mutate({
                                                        purchaserId: purchaser.purchaserId,
                                                    })
                                                }
                                            >
                                                Retry
                                            </Text>
                                        )}
                                    </YStack>
                                </XStack>
                                <XeroLink />
                            </View>
                        )}
                    </View>
                </FormSection>
            )}
        </YStack>
    )
})

export default PurchasedProperty
